import { gsap } from "gsap";
import { Observer } from "gsap/Observer";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import "@/styles/components/slider.css";
import "./product-showcase.css";

gsap.registerPlugin(ScrollTrigger, Observer);

const SLIDE_TIMING_SCALE = 0.7;
const FEATURE_TIMING_SCALE = 1.8;
const ds = (value) => value * SLIDE_TIMING_SCALE;
const df = (value) => value * FEATURE_TIMING_SCALE;
const AUTO_FIRST_FEATURE_DELAY = df(0.04);
const FEATURE_FIRST_REVEAL_DURATION = df(0.28);
const FEATURE_FADE_OUT_DURATION = df(0.32);
const FEATURE_FADE_IN_DURATION = df(0.42);
const AUTO_FEATURE_HOLD_DURATION = df(0.68);
const AUTO_FEATURE_SINGLE_HOLD_DURATION = df(0.95);
const AUTO_SCROLL_THRESHOLD = 120;
const AUTO_PIN_SCROLL_DISTANCE_MULTIPLIER = 1;
const AUTO_SKIP_ENTER_BACK_VELOCITY = 3200;
const PAGINATION_MIN_SLIDE_DURATION = 5;
const MOBILE_IMAGE_SPLIT = 10;
const DESKTOP_IMAGE_SPLIT = 10;

const readCssLengthVariable = (name) => {
  const probe = document.createElement("div");
  probe.style.position = "absolute";
  probe.style.visibility = "hidden";
  probe.style.pointerEvents = "none";
  probe.style.height = `var(${name})`;

  document.body.append(probe);
  const value = probe.getBoundingClientRect().height;
  probe.remove();

  return Number.isFinite(value) ? value : 0;
};

const getHeaderOffset = () => readCssLengthVariable("--site-header-stuck-height");
const getViewportStageHeight = () => Math.max(window.innerHeight - getHeaderOffset(), 0);
const formatSlideNumber = (value) => String(value).padStart(2, "0");
const FEATURE_DOT_PULSE_DURATION = Math.max(FEATURE_FADE_OUT_DURATION, FEATURE_FADE_IN_DURATION);
const FEATURE_DOT_PULSE_UP_DURATION = FEATURE_DOT_PULSE_DURATION * 0.3;
const FEATURE_DOT_PULSE_DOWN_DURATION = FEATURE_DOT_PULSE_DURATION - FEATURE_DOT_PULSE_UP_DURATION;

const getSlideParts = (slide) => ({
  slide,
  media: slide.querySelector("[data-showcase-media]"),
  mainImage: slide.querySelector("[data-showcase-main-image]"),
  accentImage: slide.querySelector("[data-showcase-accent-image]"),
  eyebrow: slide.querySelector("[data-showcase-eyebrow]"),
  heading: slide.querySelector("[data-showcase-heading]"),
  copy: slide.querySelector("[data-showcase-copy]"),
  button: slide.querySelector("[data-showcase-button]"),
  dot: slide.querySelector("[data-showcase-feature-dot]"),
  line: slide.querySelector("[data-showcase-feature-line]"),
  featureList: slide.querySelector(".showcase-feature-list"),
  features: gsap.utils.toArray(slide.querySelectorAll("[data-showcase-feature-item]")),
});

const getContentParts = (parts) => [parts.eyebrow, parts.heading, parts.copy, parts.button].filter(Boolean);

const setupFeatureStack = (parts) => {
  if (!parts.featureList || parts.features.length === 0) return;

  const tallestFeatureHeight = Math.max(...parts.features.map((feature) => feature.getBoundingClientRect().height), 0);

  gsap.set(parts.featureList, { height: tallestFeatureHeight > 0 ? tallestFeatureHeight : "auto" });
};

const animateIndexChange = (el, nextIndex) => {
  if (!el || el.dataset.indexValue === `${nextIndex}`) return;

  el.dataset.indexValue = `${nextIndex}`;

  gsap.killTweensOf(el);
  gsap.to(el, {
    autoAlpha: 0,
    y: -10,
    duration: 0.18,
    ease: "power1.in",
    onComplete: () => {
      el.textContent = formatSlideNumber(nextIndex + 1);
      gsap.fromTo(
        el,
        { autoAlpha: 0, y: 10 },
        {
          autoAlpha: 1,
          y: 0,
          duration: 0.28,
          ease: "power2.out",
        },
      );
    },
  });
};

const restartProgressAnimation = (el) => {
  if (!el) return;

  el.classList.remove("is-autoplay-running");
  requestAnimationFrame(() => {
    el.classList.add("is-autoplay-running");
  });
};

const setActiveSlideAccessibility = (slides, activeIndex) => {
  slides.forEach((slide, index) => {
    const isActive = index === activeIndex;
    slide.setAttribute("aria-hidden", isActive ? "false" : "true");

    if ("inert" in slide) {
      slide.inert = !isActive;
    }
  });
};

const setShowcaseInitialState = (slides, partsBySlide, activeIndex = 0) => {
  gsap.set(slides, { autoAlpha: 0, pointerEvents: "none" });
  gsap.set(slides[activeIndex], { autoAlpha: 1, pointerEvents: "auto" });
  setActiveSlideAccessibility(slides, activeIndex);
  partsBySlide.forEach((parts) => {
    const contentParts = getContentParts(parts);

    if (parts.media) gsap.set(parts.media, { autoAlpha: 0, y: 48 });
    if (parts.mainImage) gsap.set(parts.mainImage, { yPercent: 0 });
    if (parts.accentImage) gsap.set(parts.accentImage, { yPercent: 0 });
    if (contentParts.length > 0) gsap.set(contentParts, { autoAlpha: 0, y: 32 });
    if (parts.features.length > 0) gsap.set(parts.features, { autoAlpha: 0 });
    if (parts.line) gsap.set(parts.line, { scaleY: 0 });
    if (parts.dot) gsap.set(parts.dot, { scale: 0 });
  });
};

const isolateActiveSlide = (slides, activeIndex) => {
  gsap.set(slides, { autoAlpha: 0, pointerEvents: "none" });
  gsap.set(slides[activeIndex], { autoAlpha: 1, pointerEvents: "auto" });
  setActiveSlideAccessibility(slides, activeIndex);
};

const setCurrentSlideState = (slides, indexCurrent, nextIndex, { animateIndex = true } = {}) => {
  setActiveSlideAccessibility(slides, nextIndex);

  if (!indexCurrent) return;

  if (animateIndex) {
    animateIndexChange(indexCurrent, nextIndex);
    return;
  }

  indexCurrent.textContent = formatSlideNumber(nextIndex + 1);
  indexCurrent.dataset.indexValue = `${nextIndex}`;
  gsap.set(indexCurrent, { autoAlpha: 1, y: 0 });
};

const setSettledSlideState = (parts, imageSplit) => {
  const contentParts = getContentParts(parts);

  gsap.set(parts.slide, { autoAlpha: 1, pointerEvents: "auto" });
  if (parts.media) gsap.set(parts.media, { autoAlpha: 1, y: 0 });
  if (parts.mainImage) gsap.set(parts.mainImage, { yPercent: parts.accentImage ? -imageSplit : 0 });
  if (parts.accentImage) gsap.set(parts.accentImage, { yPercent: imageSplit });
  if (contentParts.length > 0) gsap.set(contentParts, { autoAlpha: 1, y: 0 });
  if (parts.line) gsap.set(parts.line, { scaleY: 1 });
  if (parts.dot) gsap.set(parts.dot, { scale: 1 });
};

const buildAutoFeatureLoop = (parts) => {
  const { features, dot } = parts;
  if (features.length <= 1) return null;

  const timeline = gsap.timeline({ paused: true, repeat: -1 });

  features.forEach((feature, index) => {
    const nextFeature = features[(index + 1) % features.length];
    timeline.to({}, { duration: AUTO_FEATURE_HOLD_DURATION });
    const transitionPosition = timeline.duration();

    timeline.to(
      feature,
      {
        autoAlpha: 0,
        duration: FEATURE_FADE_OUT_DURATION,
        ease: "power1.in",
      },
      transitionPosition,
    );

    timeline.to(
      nextFeature,
      {
        autoAlpha: 1,
        duration: FEATURE_FADE_IN_DURATION,
        ease: "power2.out",
      },
      transitionPosition,
    );

    if (dot) {
      timeline.to(
        dot,
        {
          duration: FEATURE_DOT_PULSE_DURATION,
          ease: "none",
          keyframes: [
            { scale: 1.5, ease: "power3.out", duration: FEATURE_DOT_PULSE_UP_DURATION },
            { scale: 1, ease: "power2.in", duration: FEATURE_DOT_PULSE_DOWN_DURATION },
          ],
        },
        transitionPosition,
      );
    }
  });

  return timeline;
};

const pauseAutoFeatureAnimation = (parts, { reset = false } = {}) => {
  if (parts.featureIntroTimeline) {
    if (reset) {
      parts.featureIntroTimeline.kill();
      parts.featureIntroTimeline = null;
    } else {
      parts.featureIntroTimeline.pause();
    }
  }

  if (parts.featureLoopTimeline) {
    if (reset) {
      parts.featureLoopTimeline.pause(0);
    } else {
      parts.featureLoopTimeline.pause();
    }
  }
};

const resetAutoFeatureAnimation = (parts) => {
  pauseAutoFeatureAnimation(parts, { reset: true });
  if (parts.features.length > 0) gsap.set(parts.features, { autoAlpha: 0 });
};

const ensureFeatureVisible = (parts) => {
  if (parts.features.length === 0) return;

  const hasVisibleFeature = parts.features.some((feature) => Number(gsap.getProperty(feature, "autoAlpha")) > 0.01);
  if (!hasVisibleFeature) gsap.set(parts.features[0], { autoAlpha: 1 });
};

const resumeAutoFeatureAnimation = (parts) => {
  if (parts.featureIntroTimeline) {
    parts.featureIntroTimeline.play();
    return;
  }

  if (parts.featureLoopTimeline) {
    parts.featureLoopTimeline.play();
    return;
  }

  startAutoFeatureAnimation(parts);
};

const startAutoFeatureAnimation = (parts) => {
  const { features } = parts;
  if (features.length === 0) return;

  pauseAutoFeatureAnimation(parts);
  gsap.set(features, { autoAlpha: 0 });

  if (features.length > 1 && !parts.featureLoopTimeline) {
    parts.featureLoopTimeline = buildAutoFeatureLoop(parts);
  }

  parts.featureIntroTimeline = gsap.timeline({
    onComplete: () => {
      parts.featureIntroTimeline = null;
      if (parts.featureLoopTimeline) parts.featureLoopTimeline.play(0);
    },
  });

  parts.featureIntroTimeline.to({}, { duration: AUTO_FIRST_FEATURE_DELAY });
  parts.featureIntroTimeline.to(features[0], {
    autoAlpha: 1,
    duration: FEATURE_FIRST_REVEAL_DURATION,
    ease: "power2.out",
  });
  parts.featureIntroTimeline.to(
    {},
    { duration: features.length > 1 ? AUTO_FEATURE_HOLD_DURATION : AUTO_FEATURE_SINGLE_HOLD_DURATION },
  );
};

const buildPaginationFeatureTimeline = (parts) => {
  const timeline = gsap.timeline();
  const { features, dot } = parts;

  if (features.length === 0) return timeline;

  timeline.to({}, { duration: AUTO_FIRST_FEATURE_DELAY });
  timeline.to(features[0], {
    autoAlpha: 1,
    duration: FEATURE_FIRST_REVEAL_DURATION,
    ease: "power2.out",
  });
  timeline.to(
    {},
    { duration: features.length > 1 ? AUTO_FEATURE_HOLD_DURATION : AUTO_FEATURE_SINGLE_HOLD_DURATION },
  );

  for (let index = 1; index < features.length; index += 1) {
    const transitionPosition = timeline.duration();

    timeline.to(
      features[index - 1],
      {
        autoAlpha: 0,
        duration: FEATURE_FADE_OUT_DURATION,
        ease: "power1.in",
      },
      transitionPosition,
    );

    timeline.to(
      features[index],
      {
        autoAlpha: 1,
        duration: FEATURE_FADE_IN_DURATION,
        ease: "power2.out",
      },
      transitionPosition,
    );

    if (dot) {
      timeline.to(
        dot,
        {
          duration: FEATURE_DOT_PULSE_DURATION,
          ease: "none",
          keyframes: [
            { scale: 1.5, ease: "power3.out", duration: FEATURE_DOT_PULSE_UP_DURATION },
            { scale: 1, ease: "power2.in", duration: FEATURE_DOT_PULSE_DOWN_DURATION },
          ],
        },
        transitionPosition,
      );
    }

    timeline.to({}, { duration: AUTO_FEATURE_HOLD_DURATION });
  }

  return timeline;
};

const getPaginationSlideDelayDuration = (parts) => {
  if (typeof parts.paginationSlideDelayDuration === "number") {
    return parts.paginationSlideDelayDuration;
  }

  const featureTimeline = buildPaginationFeatureTimeline(parts);
  parts.paginationSlideDelayDuration = Math.max(featureTimeline.duration(), PAGINATION_MIN_SLIDE_DURATION);
  featureTimeline.kill();

  return parts.paginationSlideDelayDuration;
};

const animateSlideIn = (
  timeline,
  parts,
  { position = ">", imageSplit = MOBILE_IMAGE_SPLIT, startFeatures = true } = {},
) => {
  const contentParts = getContentParts(parts);

  timeline.set(parts.slide, { autoAlpha: 1, pointerEvents: "auto" }, position);
  if (parts.media) timeline.set(parts.media, { autoAlpha: 0, y: 48 }, position);
  if (parts.mainImage) timeline.set(parts.mainImage, { yPercent: 0 }, position);
  if (parts.accentImage) timeline.set(parts.accentImage, { yPercent: 0 }, position);
  if (contentParts.length > 0) timeline.set(contentParts, { autoAlpha: 0, y: 32 }, position);
  if (parts.features.length > 0) timeline.set(parts.features, { autoAlpha: 0 }, position);
  if (parts.line) timeline.set(parts.line, { scaleY: 0 }, position);
  if (parts.dot) timeline.set(parts.dot, { scale: 0 }, position);

  if (parts.media) {
    timeline.to(
      parts.media,
      {
        autoAlpha: 1,
        y: 0,
        duration: ds(0.55),
        ease: "power2.out",
      },
      position,
    );
  }

  if (parts.mainImage) {
    timeline.to(
      parts.mainImage,
      {
        yPercent: parts.accentImage ? -imageSplit : 0,
        duration: ds(0.9),
        ease: "power3.out",
      },
      `<+${ds(0.08)}`,
    );
  }

  if (parts.accentImage) {
    timeline.to(
      parts.accentImage,
      {
        yPercent: imageSplit,
        duration: ds(0.9),
        ease: "power3.out",
      },
      `<+${ds(0.08)}`,
    );
  }

  if (contentParts.length > 0) {
    timeline.to(
      contentParts,
      {
        autoAlpha: 1,
        y: 0,
        duration: ds(0.5),
        stagger: ds(0.1),
        ease: "power2.out",
      },
      `<+${ds(0.12)}`,
    );
  }

  if (parts.line) {
    timeline.to(
      parts.line,
      {
        scaleY: 1,
        duration: ds(0.4),
        ease: "power2.out",
      },
      `<+${ds(0.08)}`,
    );
  }

  if (parts.dot) {
    timeline.to(
      parts.dot,
      {
        scale: 1,
        duration: ds(0.28),
        ease: "back.out(2.4)",
      },
      parts.line ? ">" : `<+${ds(0.08)}`,
    );
  }

  if (startFeatures) {
    timeline.call(() => startAutoFeatureAnimation(parts), [], `>+${ds(0.08)}`);
  }
};

const animateSlideOut = (timeline, parts, position = ">", imageSplit = MOBILE_IMAGE_SPLIT) => {
  const contentParts = getContentParts(parts);

  let mediaExitPosition = position;

  timeline.call(() => pauseAutoFeatureAnimation(parts), [], position);

  if (parts.features.length > 0) {
    timeline.to(
      parts.features,
      {
        autoAlpha: 0,
        duration: ds(0.18),
        ease: "power1.in",
      },
      position,
    );

    mediaExitPosition = ">";
  }

  if (contentParts.length > 0) {
    timeline.to(
      contentParts,
      {
        autoAlpha: 0,
        y: -24,
        duration: ds(0.28),
        stagger: ds(0.05),
        ease: "power1.in",
      },
      position,
    );

    mediaExitPosition = ">";
  }

  if (parts.dot) {
    timeline.to(
      parts.dot,
      {
        scale: 0,
        duration: ds(0.2),
        ease: "power1.in",
      },
      position,
    );

    mediaExitPosition = ">";
  }

  if (parts.line) {
    timeline.to(
      parts.line,
      {
        scaleY: 0,
        duration: ds(0.28),
        ease: "power1.in",
      },
      position,
    );

    mediaExitPosition = ">";
  }

  if (parts.media) {
    timeline.to(
      parts.media,
      {
        autoAlpha: 0,
        y: -32,
        duration: ds(0.38),
        ease: "power1.in",
      },
      mediaExitPosition,
    );
  }

  if (parts.mainImage) {
    timeline.to(
      parts.mainImage,
      {
        yPercent: parts.accentImage ? -(imageSplit * 1.5) : 0,
        duration: ds(0.38),
        ease: "power1.in",
      },
      "<",
    );
  }

  if (parts.accentImage) {
    timeline.to(
      parts.accentImage,
      {
        yPercent: imageSplit * 1.5,
        duration: ds(0.38),
        ease: "power1.in",
      },
      "<",
    );
  }
};

const initShowcase = (el, { isMobile = false } = {}) => {
  const stage = el.querySelector("[data-showcase-stage]");
  const slides = gsap.utils.toArray(el.querySelectorAll("[data-showcase-slide]"));
  const paginationWrap = el.querySelector("[data-showcase-pagination-wrap]");
  const pagination = el.querySelector("[data-showcase-pagination]");
  const paginationBullets = gsap.utils.toArray(el.querySelectorAll("[data-showcase-pagination-bullet]"));
  const indexWrap = el.querySelector("[data-showcase-index]");
  const indexCurrent = el.querySelector("[data-showcase-index-current]");
  const paginationNav = el.querySelector("[data-showcase-pagination-nav]");
  const prevButton = el.querySelector("[data-showcase-prev]");
  const nextButton = el.querySelector("[data-showcase-next]");
  const glow = el.querySelector(".showcase-glow");
  if (!stage || slides.length === 0) return;

  const partsBySlide = slides.map(getSlideParts);
  const asPagination = stage.hasAttribute("data-showcase-as-pagination");
  const imageSplit = isMobile ? MOBILE_IMAGE_SPLIT : DESKTOP_IMAGE_SPLIT;
  let activeIndex = 0;
  let leftPinnedForward = false;
  let observer = null;
  let pinTrigger = null;
  let singleSlideTrigger = null;
  let transitionTimeline = null;
  let leaveBackTimeline = null;
  let singleSlideTimeline = null;
  let lockPinnedScroll = () => {};
  let unlockPinnedScroll = () => {};

  partsBySlide.forEach((parts) => {
    setupFeatureStack(parts);
  });

  if (isMobile) {
    const tallestSlideHeight = Math.max(...slides.map((slide) => slide.offsetHeight), 0);
    const stageHeight = Math.max(tallestSlideHeight, getViewportStageHeight());
    gsap.set(el, { "--showcase-stage-height": `${Math.ceil(stageHeight)}px` });
  }

  el.classList.add("showcase-ready");

  slides.forEach((slide, index) => {
    slide.style.zIndex = `${slides.length - index}`;
  });

  setShowcaseInitialState(slides, partsBySlide, 0);
  if (indexWrap) gsap.set(indexWrap, { autoAlpha: 0, y: 18 });
  if (indexCurrent) {
    indexCurrent.textContent = formatSlideNumber(1);
    indexCurrent.dataset.indexValue = "0";
  }
  if (glow) gsap.set(glow, { autoAlpha: 0 });
  if (paginationWrap) gsap.set(paginationWrap, { autoAlpha: 0, y: 18 });
  if (paginationNav) gsap.set(paginationNav, { autoAlpha: 0, y: 18 });

  const killTransitionTimeline = () => {
    if (!transitionTimeline) return;
    transitionTimeline.kill();
    transitionTimeline = null;
  };

  const killLeaveBackTimeline = () => {
    if (!leaveBackTimeline) return;
    leaveBackTimeline.kill();
    leaveBackTimeline = null;
  };

  const killSingleSlideTimeline = () => {
    if (!singleSlideTimeline) return;
    singleSlideTimeline.kill();
    singleSlideTimeline = null;
  };

  const enableObserver = () => {
    observer?.enable();
  };

  const disableObserver = () => {
    observer?.disable();
  };

  const showPinnedChrome = (duration) => {
    if (glow) {
      gsap.to(glow, {
        autoAlpha: 0.7,
        duration,
        ease: "power2.out",
        overwrite: "auto",
      });
    }
    if (paginationWrap) {
      gsap.to(paginationWrap, {
        autoAlpha: 1,
        y: 0,
        duration: Math.min(duration, 0.45),
        ease: "power2.out",
        overwrite: "auto",
      });
    }
    if (indexWrap) {
      gsap.to(indexWrap, {
        autoAlpha: 1,
        y: 0,
        duration: Math.min(duration, 0.45),
        ease: "power2.out",
        overwrite: "auto",
      });
    }
    if (paginationNav) {
      gsap.to(paginationNav, {
        autoAlpha: 1,
        y: 0,
        duration: Math.min(duration, 0.45),
        ease: "power2.out",
        overwrite: "auto",
      });
    }
  };

  const setPaginationProgressState = (index, { animate = false } = {}) => {
    if (!pagination || paginationBullets.length === 0 || index < 0 || index >= partsBySlide.length) return;

    paginationBullets.forEach((bullet, bulletIndex) => {
      const isActive = bulletIndex === index;
      bullet.classList.toggle("swiper-pagination-bullet-active", isActive);
      bullet.setAttribute("aria-current", isActive ? "true" : "false");
    });

    pagination.style.setProperty(
      "--slider-pagination-duration",
      `${getPaginationSlideDelayDuration(partsBySlide[index]) * 1000}ms`,
    );

    if (animate) {
      restartProgressAnimation(pagination);
      return;
    }

    pagination.classList.remove("is-autoplay-running");
  };

  const addPaginationSlideDelay = (timeline, parts) => {
    const featureTimeline = buildPaginationFeatureTimeline(parts);
    const minimumHoldDuration = Math.max(PAGINATION_MIN_SLIDE_DURATION - featureTimeline.duration(), 0);

    if (featureTimeline.duration() > 0) {
      timeline.add(featureTimeline, `>+${ds(0.08)}`);
    }

    timeline.to({}, { duration: minimumHoldDuration });
  };

  const resetPinnedState = () => {
    disableObserver();
    killSingleSlideTimeline();
    killTransitionTimeline();
    killLeaveBackTimeline();
    unlockPinnedScroll();
    if (glow) gsap.set(glow, { autoAlpha: 0 });
    if (paginationWrap) gsap.set(paginationWrap, { autoAlpha: 0, y: 18 });
    if (indexWrap) gsap.set(indexWrap, { autoAlpha: 0, y: 18 });
    if (paginationNav) gsap.set(paginationNav, { autoAlpha: 0, y: 18 });
    activeIndex = 0;
    leftPinnedForward = false;
    setShowcaseInitialState(slides, partsBySlide, 0);
    partsBySlide.forEach((parts) => {
      resetAutoFeatureAnimation(parts);
    });
    if (indexCurrent) {
      indexCurrent.textContent = formatSlideNumber(1);
      indexCurrent.dataset.indexValue = "0";
      gsap.set(indexCurrent, { autoAlpha: 1, y: 0 });
    }
    setPaginationProgressState(0);
  };

  if (asPagination) {
    let paginationLoopActive = true;
    let paginationStarted = false;
    let paginationTransitionTargetIndex = 0;
    const paginationBulletCleanups = [];

    const getPaginationInteractiveIndex = () => paginationTransitionTargetIndex;

    const settlePaginationAt = (index) => {
      killSingleSlideTimeline();
      killTransitionTimeline();
      killLeaveBackTimeline();

      activeIndex = index;
      paginationTransitionTargetIndex = index;
      isolateActiveSlide(slides, index);
      setSettledSlideState(partsBySlide[index], imageSplit);
      setCurrentSlideState(slides, indexCurrent, index, { animateIndex: false });
      setPaginationProgressState(index);

      partsBySlide.forEach((parts, partsIndex) => {
        if (partsIndex === index) {
          pauseAutoFeatureAnimation(parts);
          ensureFeatureVisible(parts);
          return;
        }

        resetAutoFeatureAnimation(parts);
      });
    };

    const continuePaginationLoopFrom = (index, { restartProgress = true } = {}) => {
      if (!paginationLoopActive) return;

      activeIndex = index;
      paginationTransitionTargetIndex = index;
      isolateActiveSlide(slides, index);
      setSettledSlideState(partsBySlide[index], imageSplit);

      if (slides.length > 1) {
        const nextIndex = (index + 1) % slides.length;

        if (restartProgress) {
          setPaginationProgressState(index, { animate: true });
        }

        queuePaginationAdvance(index, nextIndex);
        return;
      }

      ensureFeatureVisible(partsBySlide[index]);
    };

    const queuePaginationAdvance = (currentIndex, nextIndex, { animateIndex = true } = {}) => {
      if (!paginationLoopActive || nextIndex === null) return;

      const currentParts = partsBySlide[currentIndex];
      const nextParts = partsBySlide[nextIndex];

      killTransitionTimeline();
      killLeaveBackTimeline();

      transitionTimeline = gsap.timeline({
        defaults: { ease: "none" },
        onComplete: () => {
          transitionTimeline = null;

          if (!paginationLoopActive) return;

          continuePaginationLoopFrom(nextIndex, { restartProgress: false });
        },
      });

      addPaginationSlideDelay(transitionTimeline, currentParts);
      transitionTimeline.call(() => {
        paginationTransitionTargetIndex = nextIndex;
      }, [], ">");
      animateSlideOut(transitionTimeline, currentParts, ">", imageSplit);
      transitionTimeline.set(currentParts.slide, { autoAlpha: 0, pointerEvents: "none" }, ">");
      transitionTimeline.call(() => resetAutoFeatureAnimation(currentParts), [], ">");
      transitionTimeline.call(() => {
        setCurrentSlideState(slides, indexCurrent, nextIndex, { animateIndex });
        setPaginationProgressState(nextIndex, { animate: true });
      });
      animateSlideIn(transitionTimeline, nextParts, {
        position: ">",
        imageSplit,
        startFeatures: false,
      });
    };

    const jumpToPaginationSlide = (targetIndex) => {
      if (!paginationLoopActive || !paginationStarted) return;
      if (targetIndex < 0 || targetIndex >= slides.length) return;

      const currentParts = partsBySlide[activeIndex];
      const nextParts = partsBySlide[targetIndex];
      paginationTransitionTargetIndex = targetIndex;

      killSingleSlideTimeline();
      killTransitionTimeline();
      killLeaveBackTimeline();

      isolateActiveSlide(slides, activeIndex);
      setSettledSlideState(currentParts, imageSplit);
      pauseAutoFeatureAnimation(currentParts);

      if (targetIndex === activeIndex) {
        setCurrentSlideState(slides, indexCurrent, activeIndex, { animateIndex: false });
        continuePaginationLoopFrom(activeIndex);
        return;
      }

      transitionTimeline = gsap.timeline({
        defaults: { ease: "none" },
        onComplete: () => {
          transitionTimeline = null;

          if (!paginationLoopActive) return;

          continuePaginationLoopFrom(targetIndex, { restartProgress: false });
        },
      });

      animateSlideOut(transitionTimeline, currentParts, 0, imageSplit);
      transitionTimeline.set(currentParts.slide, { autoAlpha: 0, pointerEvents: "none" }, ">");
      transitionTimeline.call(() => resetAutoFeatureAnimation(currentParts), [], ">");
      transitionTimeline.call(() => {
        setCurrentSlideState(slides, indexCurrent, targetIndex);
        setPaginationProgressState(targetIndex, { animate: true });
      });
      animateSlideIn(transitionTimeline, nextParts, {
        position: ">",
        imageSplit,
        startFeatures: false,
      });
    };

    const startPagination = () => {
      if (paginationStarted || !paginationLoopActive) return;

      paginationStarted = true;
      killTransitionTimeline();
      killLeaveBackTimeline();
      killSingleSlideTimeline();
      activeIndex = 0;
      paginationTransitionTargetIndex = 0;
      showPinnedChrome(0.6);
      setShowcaseInitialState(slides, partsBySlide, 0);
      setCurrentSlideState(slides, indexCurrent, 0, { animateIndex: false });
      setPaginationProgressState(0);

      singleSlideTimeline = gsap.timeline({
        defaults: { ease: "none" },
        onComplete: () => {
          singleSlideTimeline = null;
          continuePaginationLoopFrom(0);
        },
      });

      animateSlideIn(singleSlideTimeline, partsBySlide[0], {
        position: 0,
        imageSplit,
        startFeatures: slides.length === 1,
      });
    };

    singleSlideTrigger = ScrollTrigger.create({
      trigger: stage,
      start: "top center",
      invalidateOnRefresh: true,
      once: true,
      onToggle: (self) => {
        if (!self.isActive) return;
        startPagination();
      },
    });

    if (singleSlideTrigger.isActive || singleSlideTrigger.progress > 0) {
      startPagination();
    }

    paginationBullets.forEach((bullet, index) => {
      const handleClick = () => {
        settlePaginationAt(getPaginationInteractiveIndex());
        jumpToPaginationSlide(index);
      };

      bullet.addEventListener("click", handleClick);
      paginationBulletCleanups.push(() => {
        bullet.removeEventListener("click", handleClick);
      });
    });

    if (prevButton) {
      const handlePrevClick = () => {
        const currentIndex = getPaginationInteractiveIndex();
        settlePaginationAt(currentIndex);
        jumpToPaginationSlide((currentIndex - 1 + slides.length) % slides.length);
      };

      prevButton.addEventListener("click", handlePrevClick);
      paginationBulletCleanups.push(() => {
        prevButton.removeEventListener("click", handlePrevClick);
      });
    }

    if (nextButton) {
      const handleNextClick = () => {
        const currentIndex = getPaginationInteractiveIndex();
        settlePaginationAt(currentIndex);
        jumpToPaginationSlide((currentIndex + 1) % slides.length);
      };

      nextButton.addEventListener("click", handleNextClick);
      paginationBulletCleanups.push(() => {
        nextButton.removeEventListener("click", handleNextClick);
      });
    }

    return () => {
      paginationLoopActive = false;
      paginationBulletCleanups.forEach((cleanup) => cleanup());
      disableObserver();
      unlockPinnedScroll();
      observer?.kill();
      pinTrigger?.kill();
      singleSlideTrigger?.kill();
      killSingleSlideTimeline();
      killTransitionTimeline();
      killLeaveBackTimeline();
      partsBySlide.forEach((parts) => {
        resetAutoFeatureAnimation(parts);
      });
    };
  }

  if (slides.length === 1) {
    const playSingleSlideIn = () => {
      if (singleSlideTimeline) return;

      killTransitionTimeline();
      killLeaveBackTimeline();
      activeIndex = 0;
      showPinnedChrome(0.6);
      setShowcaseInitialState(slides, partsBySlide, 0);
      setCurrentSlideState(slides, indexCurrent, 0, { animateIndex: false });

      singleSlideTimeline = gsap.timeline({
        defaults: { ease: "none" },
        onComplete: () => {
          singleSlideTimeline = null;
          isolateActiveSlide(slides, 0);
          setSettledSlideState(partsBySlide[0], imageSplit);
          ensureFeatureVisible(partsBySlide[0]);
        },
      });

      animateSlideIn(singleSlideTimeline, partsBySlide[0], {
        position: 0,
        imageSplit,
      });
    };

    singleSlideTrigger = ScrollTrigger.create({
      trigger: stage,
      start: () => `top ${getHeaderOffset()}px`,
      invalidateOnRefresh: true,
      once: true,
      onToggle: (self) => {
        if (!self.isActive) return;
        playSingleSlideIn();
      },
    });

    if (singleSlideTrigger.isActive || singleSlideTrigger.progress > 0) {
      playSingleSlideIn();
    }
  } else {
    let isTransitioning = false;
    let programmaticLeaveBack = false;
    let pinnedSessionActive = false;
    let accumulatedDelta = 0;
    let scrollLockY = null;

    const preventLockedScroll = (event) => {
      if (scrollLockY === null) return;
      event.preventDefault();
    };

    const enforceScrollLock = () => {
      if (scrollLockY === null) return;
      if (Math.abs(window.scrollY - scrollLockY) < 1) return;
      window.scrollTo({
        top: scrollLockY,
        behavior: "auto",
      });
    };

    const resetAccumulatedDelta = () => {
      accumulatedDelta = 0;
    };

    unlockPinnedScroll = () => {
      if (scrollLockY === null) return;

      scrollLockY = null;
      document.documentElement.style.removeProperty("overscroll-behavior");
      window.removeEventListener("scroll", enforceScrollLock);
      window.removeEventListener("wheel", preventLockedScroll);
      window.removeEventListener("touchmove", preventLockedScroll);
    };

    lockPinnedScroll = () => {
      if (!pinTrigger?.isActive) return;

      scrollLockY = window.scrollY;
      document.documentElement.style.setProperty("overscroll-behavior", "none");
      window.addEventListener("scroll", enforceScrollLock);
      window.addEventListener("wheel", preventLockedScroll, { passive: false });
      window.addEventListener("touchmove", preventLockedScroll, { passive: false });
    };

    const settleSlide = (index, { animateIndex = false, resumeFeatures = false } = {}) => {
      const activeParts = partsBySlide[index];

      activeIndex = index;
      isolateActiveSlide(slides, index);
      setSettledSlideState(activeParts, imageSplit);
      setCurrentSlideState(slides, indexCurrent, index, { animateIndex });

      partsBySlide.forEach((parts, partsIndex) => {
        if (partsIndex === index) return;
        resetAutoFeatureAnimation(parts);
      });

      ensureFeatureVisible(activeParts);

      if (resumeFeatures) {
        resumeAutoFeatureAnimation(activeParts);
      } else {
        pauseAutoFeatureAnimation(activeParts);
      }
    };

    const playSlideIn = (index, { animateIndex = false } = {}) => {
      killTransitionTimeline();
      killLeaveBackTimeline();

      activeIndex = index;
      isTransitioning = true;
      lockPinnedScroll();
      setShowcaseInitialState(slides, partsBySlide, index);
      setCurrentSlideState(slides, indexCurrent, index, { animateIndex });

      transitionTimeline = gsap.timeline({
        defaults: { ease: "none" },
        onComplete: () => {
          transitionTimeline = null;
          isTransitioning = false;
          unlockPinnedScroll();
          resetAccumulatedDelta();
        },
      });

      animateSlideIn(transitionTimeline, partsBySlide[index], {
        position: 0,
        imageSplit,
      });
    };

    const transitionToSlide = (nextIndex, { animateIndex = true } = {}) => {
      if (isTransitioning || nextIndex === activeIndex || nextIndex < 0 || nextIndex >= slides.length) return;

      const previousParts = partsBySlide[activeIndex];
      const nextParts = partsBySlide[nextIndex];

      killTransitionTimeline();
      killLeaveBackTimeline();

      isTransitioning = true;
      lockPinnedScroll();
      transitionTimeline = gsap.timeline({
        defaults: { ease: "none" },
        onComplete: () => {
          transitionTimeline = null;
          activeIndex = nextIndex;
          isTransitioning = false;
          unlockPinnedScroll();
          resetAccumulatedDelta();
        },
      });

      animateSlideOut(transitionTimeline, previousParts, 0, imageSplit);
      transitionTimeline.set(previousParts.slide, { autoAlpha: 0, pointerEvents: "none" }, ">");
      transitionTimeline.call(() => resetAutoFeatureAnimation(previousParts), [], ">");
      transitionTimeline.call(() => {
        setCurrentSlideState(slides, indexCurrent, nextIndex, { animateIndex });
      });
      animateSlideIn(transitionTimeline, nextParts, {
        position: ">",
        imageSplit,
      });
    };

    const releaseForward = () => {
      if (!pinTrigger) return;

      disableObserver();
      killTransitionTimeline();
      killLeaveBackTimeline();
      isTransitioning = false;
      unlockPinnedScroll();
      leftPinnedForward = true;
      resetAccumulatedDelta();
      settleSlide(slides.length - 1, { animateIndex: false, resumeFeatures: false });

      requestAnimationFrame(() => {
        window.scrollTo({
          top: pinTrigger.end + 2,
          behavior: "auto",
        });
      });
    };

    const leaveBack = () => {
      if (!pinTrigger || isTransitioning) return;

      const activeParts = partsBySlide[activeIndex];

      disableObserver();
      killTransitionTimeline();
      killLeaveBackTimeline();
      unlockPinnedScroll();
      isTransitioning = true;
      resetAccumulatedDelta();

      isolateActiveSlide(slides, activeIndex);
      setSettledSlideState(activeParts, imageSplit);
      ensureFeatureVisible(activeParts);
      pauseAutoFeatureAnimation(activeParts);
      programmaticLeaveBack = true;

      requestAnimationFrame(() => {
        window.scrollTo({
          top: Math.max(pinTrigger.start - 2, 0),
          behavior: "auto",
        });
      });

      leaveBackTimeline = gsap.timeline({
        defaults: { ease: "none" },
        onComplete: () => {
          leaveBackTimeline = null;
          isTransitioning = false;
          unlockPinnedScroll();
          resetPinnedState();
        },
      });

      if (glow) {
        leaveBackTimeline.to(
          glow,
          {
            autoAlpha: 0,
            duration: 0.2,
            ease: "power1.in",
          },
          0,
        );
      }

      if (indexWrap) {
        leaveBackTimeline.to(
          indexWrap,
          {
            autoAlpha: 0,
            y: 18,
            duration: 0.2,
            ease: "power1.in",
          },
          0,
        );
      }

      animateSlideOut(leaveBackTimeline, activeParts, 0, imageSplit);
      leaveBackTimeline.call(() => resetAutoFeatureAnimation(activeParts), [], ">");
    };

    observer = Observer.create({
      target: window,
      type: isMobile ? "wheel,touch" : "wheel,touch,pointer",
      preventDefault: true,
      lockAxis: true,
      allowClicks: true,
      onChangeY: (self) => {
        if (!pinTrigger?.isActive || isTransitioning) {
          resetAccumulatedDelta();
          return;
        }

        const eventType = self.event?.type || "";
        const isTouchGesture = eventType.startsWith("touch") || self.event?.pointerType === "touch";
        const deltaY = isTouchGesture ? -self.deltaY : self.deltaY;

        accumulatedDelta += deltaY;
        if (Math.abs(accumulatedDelta) < AUTO_SCROLL_THRESHOLD) return;

        const direction = accumulatedDelta > 0 ? 1 : -1;
        resetAccumulatedDelta();

        if (direction > 0) {
          if (activeIndex >= slides.length - 1) {
            releaseForward();
            return;
          }

          transitionToSlide(activeIndex + 1);
          return;
        }

        if (activeIndex <= 0) {
          leaveBack();
          return;
        }

        transitionToSlide(activeIndex - 1);
      },
      onStop: () => {
        resetAccumulatedDelta();
      },
    });

    disableObserver();

    pinTrigger = ScrollTrigger.create({
      trigger: stage,
      pin: true,
      start: () => `top ${getHeaderOffset()}px`,
      end: () => `+=${window.innerHeight * Math.max(slides.length * AUTO_PIN_SCROLL_DISTANCE_MULTIPLIER, 1)}`,
      invalidateOnRefresh: true,
      onEnter: () => {
        if (pinnedSessionActive) return;

        pinnedSessionActive = true;
        leftPinnedForward = false;
        programmaticLeaveBack = false;
        resetAccumulatedDelta();
        showPinnedChrome(0.6);
        enableObserver();
        playSlideIn(0, { animateIndex: false });
      },
      onEnterBack: (self) => {
        killTransitionTimeline();
        killLeaveBackTimeline();
        resetAccumulatedDelta();

        if (self.getVelocity() <= -AUTO_SKIP_ENTER_BACK_VELOCITY) {
          programmaticLeaveBack = true;
          pinnedSessionActive = false;
          resetPinnedState();

          requestAnimationFrame(() => {
            window.scrollTo({
              top: Math.max(pinTrigger.start - 2, 0),
              behavior: "auto",
            });
          });
          return;
        }

        showPinnedChrome(0.45);

        if (leftPinnedForward) {
          pinnedSessionActive = true;
          settleSlide(slides.length - 1, { animateIndex: false, resumeFeatures: true });
          enableObserver();
          leftPinnedForward = false;
          return;
        }

        if (pinnedSessionActive) {
          showPinnedChrome(0.45);
          enableObserver();
          return;
        }

        pinnedSessionActive = true;
        enableObserver();
        playSlideIn(0, { animateIndex: false });
      },
      onLeave: () => {
        pinnedSessionActive = false;
        leftPinnedForward = true;
        disableObserver();
        killTransitionTimeline();
        killLeaveBackTimeline();
        isTransitioning = false;
        unlockPinnedScroll();
        resetAccumulatedDelta();
        settleSlide(slides.length - 1, { animateIndex: false, resumeFeatures: false });
      },
      onLeaveBack: () => {
        pinnedSessionActive = false;
        disableObserver();
        unlockPinnedScroll();
        resetAccumulatedDelta();

        if (programmaticLeaveBack) {
          programmaticLeaveBack = false;
          return;
        }

        resetPinnedState();
      },
    });
  }

  return () => {
    disableObserver();
    unlockPinnedScroll();
    observer?.kill();
    pinTrigger?.kill();
    singleSlideTrigger?.kill();
    killSingleSlideTimeline();
    killTransitionTimeline();
    killLeaveBackTimeline();
    partsBySlide.forEach((parts) => {
      resetAutoFeatureAnimation(parts);
    });
  };
};

document.addEventListener("DOMContentLoaded", () => {
  const mm = gsap.matchMedia();

  mm.add("(min-width: 768px) and (prefers-reduced-motion: no-preference)", () => {
    const cleanups = [];

    document.querySelectorAll(".element-productshowcase").forEach((el) => {
      let instanceCleanup;
      const ctx = gsap.context(() => {
        instanceCleanup = initShowcase(el);
      }, el);

      cleanups.push(() => {
        instanceCleanup?.();
        ctx.revert();
        el.classList.remove("showcase-ready");
        el.style.removeProperty("--showcase-stage-height");
        el.querySelectorAll("[data-showcase-slide]").forEach((slide) => {
          slide.removeAttribute("aria-hidden");
          if ("inert" in slide) slide.inert = false;
        });
      });
    });

    return () => {
      cleanups.forEach((cleanup) => cleanup());
    };
  });

  mm.add("(max-width: 767px) and (prefers-reduced-motion: no-preference)", () => {
    const cleanups = [];

    document.querySelectorAll(".element-productshowcase").forEach((el) => {
      let instanceCleanup;
      const ctx = gsap.context(() => {
        instanceCleanup = initShowcase(el, { isMobile: true });
      }, el);

      cleanups.push(() => {
        instanceCleanup?.();
        ctx.revert();
        el.classList.remove("showcase-ready");
        el.style.removeProperty("--showcase-stage-height");
        el.querySelectorAll("[data-showcase-slide]").forEach((slide) => {
          slide.removeAttribute("aria-hidden");
          if ("inert" in slide) slide.inert = false;
        });
      });
    });

    return () => {
      cleanups.forEach((cleanup) => cleanup());
    };
  });
});
